/**
 * /well-servicing/invoices/new ("New invoice") — customer-approved field tickets on no
 * live invoice (GET /well-servicing/invoices/billable, fieldiq-api#1295).
 *
 * ONE PANEL PER GROUP, in the API's order. A group is the set one invoice may
 * bill: one customer, one province or state, one currency. The office ticks
 * tickets in ONE group and generates (`{ ticket_ids, due_date?, notes? }`).
 * While a group holds a pick, every other group's boxes are disabled and say
 * why — the API refuses a mixed selection (`tickets_different_customers`,
 * `tickets_mixed_currency`, `tickets_different_jurisdictions`), so the screen
 * never builds one.
 *
 * A refusal is a sentence that names what it carries — the tickets, the
 * provinces, the invoice numbers — in the refusal style, never as a success.
 * `?ticket=<id>` (the ticket page's link) pre-ticks that ticket. A new invoice
 * opens on its own page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useGenerateWsInvoice, useWsBillable } from '../../../hooks/useWsInvoices';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { wsScopeText } from '../../../lib/ws-tickets';
import {
  canWriteWsInvoices,
  wsBillableGroupKey,
  wsGroupPickable,
  wsInvoiceRefusal,
  wsPrunePick,
  wsTogglePick,
  WS_NO_PICK,
  type WsInvoicePick,
} from '../../../lib/ws-invoices';
import type { WsBillableGroup } from '../../../types/ws-invoices';
import { useBackToList, useOpenRecord } from '../../downhole/recordPage';
import { WS_DATE_INPUT, WS_EYEBROW, WS_INPUT, WS_PRIMARY, WS_SECONDARY, WsEmpty, WsField, WsFormError, WsLoading, WsPanel } from '../WsBits';

const groupTitle = (g: WsBillableGroup) => g.customerName || '—';
const groupSub = (g: WsBillableGroup) => `${g.provinceState || 'Province or state not set'} · ${g.currency || '—'}`;

function GroupPanel({
  group,
  pick,
  canWrite,
  onToggle,
  onAll,
}: {
  group: WsBillableGroup;
  pick: WsInvoicePick;
  canWrite: boolean;
  onToggle: (ticketId: string) => void;
  onAll: (ids: string[]) => void;
}) {
  const openRecord = useOpenRecord();
  const key = wsBillableGroupKey(group);
  const pickable = wsGroupPickable(pick, key);
  const ids = group.tickets.map((t) => t.ticketId);
  return (
    <WsPanel
      eyebrow={groupSub(group)}
      title={groupTitle(group)}
      testId="ws-billable-group"
      actions={
        canWrite && pickable ? (
          <button type="button" className={WS_SECONDARY} onClick={() => onAll(ids)} data-testid="ws-billable-select-all">
            Select all {group.tickets.length}
          </button>
        ) : undefined
      }
      footer={`${group.tickets.length} ticket${group.tickets.length === 1 ? '' : 's'} · ${fmtCurrency(group.subtotal, group.currency)} before tax`}
    >
      {canWrite && !pickable && (
        <p data-testid="ws-billable-locked" className="border-b border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
          One invoice bills one customer, one province or state and one currency. Clear the tickets picked in the other group to pick these.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-left">
          <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            <tr className="border-l-4 border-l-transparent">
              <th scope="col" className="w-[44px] px-4 py-3"><span className="sr-only">Select</span></th>
              <th scope="col" className="px-4 py-3">Ticket</th>
              <th scope="col" className="px-4 py-3">Job</th>
              <th scope="col" className="px-4 py-3">Covers</th>
              <th scope="col" className="px-4 py-3">Approved by</th>
              <th scope="col" className="px-4 py-3">PO</th>
              <th scope="col" className="px-4 py-3 text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {group.tickets.map((t) => (
              <tr key={t.ticketId} className="border-t border-l-4 border-fieldiq-border border-l-fi-green" data-testid="ws-billable-ticket">
                <td className="px-4 py-2.5">
                  {canWrite && (
                    <input
                      type="checkbox"
                      className="size-4 accent-fi-amber disabled:opacity-40"
                      checked={pick.ids.includes(t.ticketId)}
                      disabled={!pickable}
                      onChange={() => onToggle(t.ticketId)}
                      aria-label={`Select ${t.ticketNumber}`}
                      data-testid="ws-billable-select"
                    />
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <button
                    type="button"
                    className="font-mono text-sm font-extrabold text-white underline-offset-2 hover:underline"
                    onClick={() => openRecord(`/well-servicing/tickets/${t.ticketId}`)}
                  >
                    {t.ticketNumber || '—'}
                  </button>
                </td>
                <td className="px-4 py-2.5">
                  <Link to={`/well-servicing/jobs/${t.jobId}?tab=tickets`} className="font-mono text-sm text-fieldiq-text underline-offset-2 hover:underline">
                    {t.jobNumber || '—'}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-sm text-fieldiq-text">{wsScopeText({ scope: t.scope, workDate: t.workDate })}</td>
                <td className="px-4 py-2.5 text-sm text-fieldiq-text">{t.customerApprovedByName || '—'}</td>
                <td className="px-4 py-2.5 font-mono text-sm text-fieldiq-text">{t.poNumber || '—'}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(t.subtotal, group.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </WsPanel>
  );
}

export default function WsNewInvoiceScreen() {
  const back = useBackToList('/well-servicing/invoices');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const canWrite = canWriteWsInvoices(user?.role);
  const billable = useWsBillable();
  const generate = useGenerateWsInvoice();
  const failure = queryLoadFailure(billable);
  const groups = useMemo(() => billable.data?.data ?? [], [billable.data]);
  const [rawPick, setPick] = useState<WsInvoicePick>(WS_NO_PICK);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const pick = useMemo(() => wsPrunePick(rawPick, groups), [rawPick, groups]);
  const ticketCount = groups.reduce((n, g) => n + g.tickets.length, 0);
  const pickedGroup = groups.find((g) => wsBillableGroupKey(g) === pick.groupKey) ?? null;
  const numberOf = (id: string) => {
    for (const g of groups) {
      const t = g.tickets.find((x) => x.ticketId === id);
      if (t) return t.ticketNumber;
    }
    return id;
  };

  // `?ticket=` from a ticket page: tick it once the list has loaded.
  const preselect = params.get('ticket');
  const [preselected, setPreselected] = useState(false);
  useEffect(() => {
    if (preselected || !preselect || !groups.length || !canWrite) return;
    const g = groups.find((x) => x.tickets.some((t) => t.ticketId === preselect));
    if (g) setPick({ groupKey: wsBillableGroupKey(g), ids: [preselect] });
    setPreselected(true);
  }, [preselect, groups, preselected, canWrite]);

  const run = () => {
    setRefusal(null);
    if (!pick.ids.length) return setRefusal('Tick the field tickets to put on this invoice.');
    generate.mutate(
      { ticket_ids: pick.ids, ...(dueDate ? { due_date: dueDate } : {}), ...(notes.trim() ? { notes: notes.trim() } : {}) },
      {
        onSuccess: (inv) => {
          setPick(WS_NO_PICK);
          toast.success(inv ? `${inv.invoiceNumber} created as a draft` : 'Invoice created');
          if (inv) navigate(`/well-servicing/invoices/${inv.id}`, { state: { listUrl: '/well-servicing/invoices' } });
        },
        onError: (err) => setRefusal(wsInvoiceRefusal(err, 'The invoice was not created.', numberOf)),
      },
    );
  };

  return (
    <div data-testid="ws-new-invoice-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title="New invoice"
        onBack={back}
        backLabel="Back to invoices"
        stats={[
          { icon: <Receipt className="size-4" aria-hidden="true" />, label: 'Tickets', sub: 'Approved, unbilled', tone: 'accent', loading: billable.isLoading, value: displayValue(billable.isLoading || failure ? undefined : ticketCount) },
          { icon: <Receipt className="size-4" aria-hidden="true" />, label: 'Groups', sub: 'One invoice each', tone: 'neutral', loading: billable.isLoading, value: displayValue(billable.isLoading || failure ? undefined : groups.length) },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            Field tickets the customer has approved and no invoice bills yet, grouped the way one invoice may bill them: one customer, one province or state, one currency.
          </p>
          {canWrite && groups.length > 0 && (
            <WsPanel eyebrow="New invoice" title={pickedGroup ? `${pick.ids.length} ticket${pick.ids.length === 1 ? '' : 's'} for ${groupTitle(pickedGroup)}` : 'Tick the tickets to invoice'} testId="ws-generate">
              <div className="space-y-3 px-4 py-4">
                <WsFormError message={refusal} />
                {pickedGroup && (
                  <p data-testid="ws-generate-picked" className="text-sm text-fieldiq-text">
                    {pick.ids.map(numberOf).join(', ')} · {groupSub(pickedGroup)}
                  </p>
                )}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <WsField label="Due date" hint="Optional. Left blank, the customer's payment terms set it.">
                    <input className={WS_DATE_INPUT} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="ws-generate-due" />
                  </WsField>
                  <WsField label="Notes" hint="Optional. Printed on the invoice.">
                    <textarea className={`${WS_INPUT} h-20 py-2`} maxLength={10000} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ws-generate-notes" />
                  </WsField>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  {pick.ids.length > 0 && (
                    <button type="button" className={WS_SECONDARY} onClick={() => { setPick(WS_NO_PICK); setRefusal(null); }} data-testid="ws-generate-clear">
                      Clear
                    </button>
                  )}
                  <button type="button" className={WS_PRIMARY} disabled={generate.isPending || pick.ids.length === 0} onClick={run} data-testid="ws-generate-submit">
                    <Receipt className="size-4" aria-hidden="true" />
                    {generate.isPending ? 'Creating…' : 'Create draft invoice'}
                  </button>
                </div>
              </div>
            </WsPanel>
          )}
          {!canWrite && groups.length > 0 && <p className="text-sm text-fieldiq-muted">Only an admin or office staff can create an invoice.</p>}
          {billable.isLoading ? (
            <WsLoading label="Loading tickets ready to invoice" />
          ) : failure ? (
            <WsPanel eyebrow="Approved field tickets" title="New invoice">
              <LoadFailureNotice error={queryFailureError(billable)} kind={failure} subject="tickets ready to invoice" />
            </WsPanel>
          ) : groups.length === 0 ? (
            <WsPanel eyebrow="Approved field tickets" title="New invoice" testId="ws-billable-empty">
              <WsEmpty>No customer-approved field tickets are waiting to be invoiced.</WsEmpty>
            </WsPanel>
          ) : (
            groups.map((g) => (
              <GroupPanel
                key={wsBillableGroupKey(g)}
                group={g}
                pick={pick}
                canWrite={canWrite}
                onToggle={(id) => { setRefusal(null); setPick((p) => wsTogglePick(wsPrunePick(p, groups), wsBillableGroupKey(g), id)); }}
                onAll={(ids) => { setRefusal(null); setPick({ groupKey: wsBillableGroupKey(g), ids }); }}
              />
            ))
          )}
        </div>
      </section>
    </div>
  );
}
